'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface EmissionCalculatorProps {
  currentLanguage: string;
}

const EmissionCalculator = ({ currentLanguage }: EmissionCalculatorProps) => {
  const [calculatorType, setCalculatorType] = useState<'activity' | 'spend'>('activity');
  const [formData, setFormData] = useState({
    activity: '',
    quantity: '',
    unit: 'kWh',
    category: 'electricity',
  });
  const [result, setResult] = useState<number | null>(null);

  const categories = [
    { value: 'electricity', label: 'Electricity Consumption', factor: 0.4 },
    { value: 'natural-gas', label: 'Natural Gas', factor: 0.2 },
    { value: 'diesel', label: 'Diesel Fuel', factor: 2.7 },
    { value: 'flights', label: 'Air Travel', factor: 0.15 },
    { value: 'vehicles', label: 'Vehicle Transport', factor: 0.17 },
  ];

  const units = {
    electricity: ['kWh', 'MWh', 'GWh'],
    'natural-gas': ['m³', 'therms', 'GJ'],
    diesel: ['liters', 'gallons', 'tonnes'],
    flights: ['km', 'miles', 'hours'],
    vehicles: ['km', 'miles'],
  };

  const handleCalculate = () => {
    const quantity = parseFloat(formData.quantity);
    if (!isNaN(quantity)) {
      const category = categories.find((c) => c.value === formData.category);
      const emissions = quantity * (category?.factor || 1);
      setResult(emissions);
    }
  };

  return (
    <section id="emission-calculator" className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-text-primary mb-4">
            Interactive Emission Calculator
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Calculate emissions with smart validation and benchmarking against industry standards
          </p>
        </div>

        {/* Calculator Type Toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-card border border-border rounded-lg p-1">
            <button
              onClick={() => setCalculatorType('activity')}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                calculatorType === 'activity' ?'bg-primary text-primary-foreground shadow-natural' :'text-text-secondary hover:text-text-primary'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Icon name="CpuChipIcon" size={20} />
                <span>Activity-Based</span>
              </div>
            </button>
            <button
              onClick={() => setCalculatorType('spend')}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                calculatorType === 'spend' ?'bg-primary text-primary-foreground shadow-natural' :'text-text-secondary hover:text-text-primary'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Icon name="CurrencyDollarIcon" size={20} />
                <span>Spend-Based</span>
              </div>
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Calculator Form */}
          <div className="bg-card border border-border rounded-xl p-8">
            <h3 className="font-heading font-bold text-xl text-text-primary mb-6">
              {calculatorType === 'activity' ? 'Activity Data Input' : 'Spend Data Input'}
            </h3>

            <div className="space-y-6">
              {/* Category Selection */}
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  Emission Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value, unit: units[e.target.value as keyof typeof units]?.[0] || 'kWh' })}
                  className="w-full px-4 py-3 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary transition-colors"
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Quantity Input */}
              <div>
                <label className="block text-sm font-semibold text-text-primary mb-2">
                  {calculatorType === 'activity' ? 'Activity Quantity' : 'Spend Amount'}
                </label>
                <div className="flex space-x-2">
                  <input
                    type="number"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                    placeholder="Enter value"
                    className="flex-grow px-4 py-3 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary transition-colors"
                  />
                  <select
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    className="px-4 py-3 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary transition-colors"
                  >
                    {(units[formData.category as keyof typeof units] || ['kWh']).map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Calculate Button */}
              <button
                onClick={handleCalculate}
                className="w-full px-6 py-4 bg-conversion text-conversion-foreground font-heading font-semibold rounded-lg hover:bg-conversion/90 shadow-natural hover:shadow-natural-lg transition-all duration-300"
              >
                <div className="flex items-center justify-center space-x-2">
                  <Icon name="CalculatorIcon" size={20} />
                  <span>Calculate Emissions</span>
                </div>
              </button>

              {/* Result Display */}
              {result !== null && (
                <div className="bg-primary/5 border-2 border-primary rounded-xl p-6 animate-fade-in">
                  <div className="text-center">
                    <div className="text-sm font-semibold text-primary mb-2">Estimated Emissions</div>
                    <div className="text-4xl font-heading font-bold text-primary mb-1">
                      {result.toFixed(2)}
                    </div>
                    <div className="text-sm text-text-secondary">tonnes CO₂e</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Benchmarking & Insights */}
          <div className="space-y-6">
            {/* Industry Benchmark */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h4 className="font-heading font-bold text-lg text-text-primary mb-4">
                Industry Benchmark Comparison
              </h4>

              <div className="space-y-4">
                {[
                  { label: 'Your Performance', value: 85, color: 'primary' },
                  { label: 'Industry Average', value: 65, color: 'warning' },
                  { label: 'Best in Class', value: 95, color: 'success' },
                ].map((benchmark, index) => (
                  <div key={index}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-text-primary">{benchmark.label}</span>
                      <span className={`text-sm font-bold text-${benchmark.color}`}>{benchmark.value}%</span>
                    </div>
                    <div className="w-full bg-border rounded-full h-2.5 overflow-hidden">
                      <div
                        className={`bg-${benchmark.color} h-full rounded-full transition-all duration-1000`}
                        style={{ width: `${benchmark.value}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Facts */}
            <div className="bg-gradient-to-br from-info/10 to-primary/10 border border-info/20 rounded-xl p-6">
              <div className="flex items-start space-x-3 mb-4">
                <Icon name="LightBulbIcon" size={24} className="text-info flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-text-primary mb-2">Did You Know?</h4>
                  <p className="text-sm text-text-secondary">
                    Activity-based tracking provides up to 85% more accuracy than spend-based methods, enabling better decision-making for emission reduction strategies.
                  </p>
                </div>
              </div>
            </div>

            {/* Export Options */}
            <div className="bg-card border border-border rounded-xl p-6">
              <h4 className="font-heading font-bold text-lg text-text-primary mb-4">
                Export Results
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'BRSR Format', icon: 'DocumentTextIcon' },
                  { label: 'CSRD Format', icon: 'DocumentTextIcon' },
                  { label: 'Excel Report', icon: 'TableCellsIcon' },
                  { label: 'PDF Summary', icon: 'DocumentArrowDownIcon' },
                ].map((format, index) => (
                  <button
                    key={index}
                    className="px-4 py-3 bg-surface border border-border rounded-lg text-sm font-medium text-text-primary hover:border-primary hover:bg-primary/5 transition-all duration-300"
                  >
                    <div className="flex items-center space-x-2">
                      <Icon name={format.icon} size={16} />
                      <span>{format.label}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EmissionCalculator;